import React from "react";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/system";

import { DATE_FORMAT, HOURS_12_FORMAT_WITH_DATE } from "../../../../constants";
import { IColum } from "../../../../utils/copyToClipboardInXlsFormat";
import { CENTS_IN_DOLLAR } from "../../../../constants";
import { IntlFormatUSD } from "../../../../utils";
import { IWithdrawalsRequestResponse } from "../../../../api/fundedCompliance/index.types";
import {
  ComplianceWithdrawalStatusSelect,
  WithdrawalPaymentStatus,
} from "../../../../components/ui";
import EntriesIdWithPrefix, { getEntriesIdWithPrefix } from "../EntriesIdWithPrefix";
import { timeAgo, getTimeByAmericaChicagoTz } from "../../../../utils/moment";

const StyledTypography = styled(Typography)({ fontSize: "12px" });

type IWatchlistNote =
  IWithdrawalsRequestResponse["data"]["withdrawals"]["rows"][0]["compliance_traders_accounts"];
export const WITHDRAWALS_COLUMNS: Array<IColum> = [
  {
    isSortable: true,
    by: "compliance_status",
    title: "Compliance status",
    titleComponent: <Typography fontSize="12px">Compliance status</Typography>,
    path: "compliance_status",
    //@ts-ignore
    getCellValue: (
      complianceStatus: string,
      record: IWithdrawalsRequestResponse["data"]["withdrawals"]["rows"][0]
    ): React.ReactElement => {
      return (
        <ComplianceWithdrawalStatusSelect
          compliance_status={complianceStatus}
          payment_status={record.status}
          id={record.id}
        />
      );
    },
  },
  {
    isSortable: true,
    by: "compliance_status_changed_timestamp",
    title: "Status changed",
    titleComponent: (
      <Box>
        <StyledTypography>Status</StyledTypography>
        <StyledTypography>changed</StyledTypography>
      </Box>
    ),
    path: "compliance_status_changed_timestamp",
    //@ts-ignore
    getCellValue: (complianceStatusChangedTimestamp: string) => {
      if (complianceStatusChangedTimestamp) {
        const [date, time] = getTimeByAmericaChicagoTz(
          complianceStatusChangedTimestamp,
          true
        ).split(" ");

        return (
          <Box>
            <StyledTypography>{date}</StyledTypography>
            <StyledTypography>{time}</StyledTypography>
            <StyledTypography>{timeAgo(complianceStatusChangedTimestamp)}</StyledTypography>
          </Box>
        );
      }
      return "";
    },
  },
  {
    isSortable: true,
    by: "compliance_entries_id",
    title: "Entries ID",
    titleComponent: (
      <>
        <StyledTypography>Entries</StyledTypography>
        <StyledTypography>ID</StyledTypography>
      </>
    ),
    path: "compliance_traders_accounts",
    //@ts-ignore
    getCellValue: (value: IWatchlistNote): React.ReactElement => {
      return <EntriesIdWithPrefix entriesId={value[0]?.compliance_entries_id as number} />;
    },
    //@ts-ignore
    copyToClipBoardCallBack: (value: IWatchlistNote): string =>
      getEntriesIdWithPrefix(value[0]?.compliance_entries_id as number),
  },
  {
    isSortable: true,
    by: "e2t_account_id",
    title: "Funded account name",
    titleComponent: (
      <>
        <StyledTypography>Funded</StyledTypography>
        <StyledTypography>account name</StyledTypography>
      </>
    ),
    path: "traded_account.e2t_account_id",
  },
  {
    isSortable: true,
    by: "name",
    title: "Name",
    titleComponent: <StyledTypography>Name</StyledTypography>,
    path: "account.name",
    getCellValue: (name) => {
      return <StyledTypography sx={{ textWrap: "auto" }}>{name as string}</StyledTypography>;
    },
  },
  {
    isSortable: true,
    by: "email",
    title: "Email",
    titleComponent: <StyledTypography>Email</StyledTypography>,
    path: "account.email",
  },
  {
    isSortable: true,
    by: "amount_requested",
    title: "Amount requested",
    titleComponent: (
      <>
        <StyledTypography>Amount</StyledTypography>
        <StyledTypography>requested</StyledTypography>
      </>
    ),
    path: "amount_requested",
    getCellValue: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),

    copyToClipBoardCallBack: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),
  },
  {
    isSortable: true,
    by: "w80",
    title: "80%",
    titleComponent: <StyledTypography>80%</StyledTypography>,
    path: "w80",
    getCellValue: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),
    copyToClipBoardCallBack: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),
  },
  {
    isSortable: true,
    by: "w20",
    title: "20%",
    titleComponent: <StyledTypography>20%</StyledTypography>,
    path: "w20",
    getCellValue: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),
    //@ts-ignore
    copyToClipBoardCallBack: (amountRequested) => IntlFormatUSD(+amountRequested / CENTS_IN_DOLLAR),
  },
  {
    isSortable: true,
    by: "initialization_date",
    title: "Payout day",
    titleComponent: (
      <>
        <StyledTypography>Payout</StyledTypography>
        <StyledTypography>day</StyledTypography>
      </>
    ),
    path: "initialization_date",
    //@ts-ignore
    getCellValue: (initializationDate: string) => {
      const [date, time] = moment(initializationDate).format(HOURS_12_FORMAT_WITH_DATE).split(" ");
      return (
        <Box>
          <StyledTypography>{date}</StyledTypography>
          <StyledTypography>{time}</StyledTypography>
        </Box>
      );
    },
    copyToClipBoardCallBack: (initializationDate) =>
      moment(initializationDate)
        // .tz("America/Chicago")
        .format(HOURS_12_FORMAT_WITH_DATE),
  },
  {
    isSortable: true,
    by: "changed_status_time",
    title: "Changed date",
    titleComponent: (
      <>
        <StyledTypography>Changed</StyledTypography>
        <StyledTypography>date</StyledTypography>
      </>
    ),
    path: "compliance_status_changed_timestamp",
    getCellValue: (changedStatusTime) => {
      if (changedStatusTime) {
        const [date, time] = moment(changedStatusTime).format(HOURS_12_FORMAT_WITH_DATE).split(" ");
        return (
          <Box>
            <StyledTypography>{date}</StyledTypography>
            <StyledTypography>{time}</StyledTypography>
          </Box>
        );
      }

      return "";
    },
    copyToClipBoardCallBack: (changedStatusTime) =>
      changedStatusTime
        ? moment(changedStatusTime as string)
            // .tz("America/Chicago")
            .format(HOURS_12_FORMAT_WITH_DATE)
        : "",
  },
  {
    isSortable: true,
    by: "status",
    title: "Payment status",
    titleComponent: (
      <>
        <StyledTypography>Payment</StyledTypography>
        <StyledTypography>status</StyledTypography>
      </>
    ),
    path: "status",
    //@ts-ignore
    getCellValue: (status: string): React.ReactElement => {
      return (
        <Box maxWidth="60px" sx={{ textWrap: "auto" }}>
          <WithdrawalPaymentStatus status={status as string} />
        </Box>
      );
    },
  },

  {
    by: "",
    title: "Measure taken",
    titleComponent: (
      <>
        <StyledTypography>Measure</StyledTypography>
        <StyledTypography>taken</StyledTypography>
      </>
    ),
    path: "compliance_traders_accounts",
    //@ts-ignore
    getCellValue: (value: IWatchlistNote): React.ReactElement => {
      return (
        <Box display="flex" height="100%" flexDirection="column" justifyContent="space-between">
          {value.map(({ watchlist_notes }) => {
            return watchlist_notes.map(({ id, reason, measure_taken, measure_timestamp }) => {
              return (
                <StyledTypography mt={1} display="block" sx={{ textWrap: "auto" }} key={id}>
                  {moment(measure_timestamp).format(DATE_FORMAT)} - {measure_taken}
                </StyledTypography>
              );
            });
          })}
        </Box>
      );
    },
    //@ts-ignore
    copyToClipBoardCallBack: (value: IWatchlistNote): string[] => {
      return value.map(({ watchlist_notes }) => {
        return watchlist_notes.map(({ measure_taken }) => measure_taken).join(", ");
      });
    },
  },
  // {
  //   by: "",
  //   title: "Measure timestamp",
  //   titleComponent: (
  //     <>
  //       <StyledTypography >Measure</StyledTypography>
  //       <StyledTypography >timestamp</StyledTypography>
  //     </>
  //   ),
  //   path: "compliance_traders_accounts",
  //   //@ts-ignore
  //   getCellValue: (value: IWatchlistNote): React.ReactElement => {
  //     return (
  //       <Box display="flex" height="100%" flexDirection="column" justifyContent="space-around">
  //         {value.map(({ watchlist_notes }) => {
  //           return watchlist_notes.map(({ id, reason, measure_taken, measure_timestamp }) => {
  //             return (
  //               <StyledTypography  mt={1} key={id}>
  //                 {moment(measure_timestamp).format(DATE_FORMAT)}
  //               </StyledTypography>
  //             );
  //           });
  //         })}
  //       </Box>
  //     );
  //   },
  //   //@ts-ignore
  //   copyToClipBoardCallBack: (value: IWatchlistNote): string[] => {
  //     return value.map(({ watchlist_notes }) => {
  //       return watchlist_notes.map(({ measure_timestamp }) => measure_timestamp).join(", ");
  //     });
  //   },
  // },
];
